import { Component, ChangeDetectionStrategy, input, output, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IconsModule, SVGIcon } from '@progress/kendo-angular-icons';
import { SwitchModule } from '@progress/kendo-angular-inputs';
import { MesFormService, MesFormData } from './services/form.service';

@Component({
  selector: 'app-mes-service',
  standalone: true,
  templateUrl: './mes-service.component.html',
  styleUrls: ['./mes-service.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MesFormService],
  imports: [CommonModule, ReactiveFormsModule, InputsModule, LabelModule, ButtonsModule, IconsModule, SwitchModule],
})
export class MesServiceComponent {
  private formService = inject(MesFormService);

  // Input/Output - simplified
  public initialData = input<MesFormData | null>(null); // Optional initial data for editing
  public dataUpdate = output<MesFormData>();
  public validityUpdate = output<boolean>();
  public isNarrowLayout = input<boolean>(false);

  // Form - self-managed
  public mesForm = this.formService.createMesForm();

  // Service data
  public mesServiceOptions = this.formService.getServiceOptions();

  constructor() {
    this.initializeForm();
  }

  private initializeForm(): void {
    // Initialize with existing data if available
    const initialData = this.initialData();
    if (initialData) {
      this.mesForm = this.formService.createMesForm(initialData);
    }

    // Watch for form changes and emit updates
    this.mesForm.valueChanges.subscribe((value) => {
      const formData = this.formService.getFormData(this.mesForm);
      this.dataUpdate.emit(formData);
      this.validityUpdate.emit(this.mesForm.valid);
    });

    // Emit initial validity state
    this.validityUpdate.emit(this.mesForm.valid);
  }

  public isServiceSelected(serviceValue: string): boolean {
    return this.mesForm.get('selectedService')?.value === serviceValue;
  }

  public selectService(serviceValue: string): void {
    this.formService.selectService(this.mesForm, serviceValue);
  }

  public getServiceDescription(serviceValue: string): string {
    return this.formService.getServiceDescription(serviceValue);
  }

  public onTotalMachinesChange(value: number | null): void {
    this.formService.onTotalMachinesChange(this.mesForm, value);
  }

  // Debug method - call this from browser console to check form state
  public debugFormState(): void {
    this.formService.debugFormState(this.mesForm);
  }
}
